'use client';

import { cn } from '@/lib/utils';
import { DropdownOverlayPanel } from '@/shared-ui/molecules/dropdown-overlay-panel';
import { createPortal } from 'react-dom';
import type { ComponentPropsWithoutRef, CSSProperties, ReactNode, RefObject } from 'react';
import { useAnchoredFixedPanelPosition } from '../../../lib/use-anchored-fixed-panel-position';

export type SubmenuPanelItem = {
    itemKey: string;
    content: ReactNode;
};

type SubmenuPanelProps = ComponentPropsWithoutRef<'div'> & {
    activeItemKey?: string;
    panels: SubmenuPanelItem[];
    onClose: () => void;
    ignoreRefs?: Array<RefObject<HTMLElement | null>>;
    /** Nav bar element — panel is portaled to `document.body` and fixed below this anchor. */
    anchorElement?: HTMLElement | null;
};

export const SubmenuPanel = ({
    activeItemKey,
    panels,
    onClose,
    ignoreRefs,
    anchorElement = null,
    className,
    style,
    ...props
}: SubmenuPanelProps) => {
    const activePanel = panels.find((panel) => panel.itemKey === activeItemKey);
    const open = Boolean(activePanel);
    const position = useAnchoredFixedPanelPosition(open, anchorElement);

    if (!open || !position) {
        return null;
    }

    const panelStyle: CSSProperties = {
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: position.width,
        ...style,
    };

    const panel = (
        <DropdownOverlayPanel
            open
            onClose={onClose}
            ignoreRefs={ignoreRefs}
            className={cn('hidden lg:block z-40 bg-snk-bg-grey', className)}
            style={panelStyle}
            {...props}>
            <nav className="mx-auto w-full" aria-label="Submenu panel">
                {activePanel?.content}
            </nav>
        </DropdownOverlayPanel>
    );

    return createPortal(panel, document.body);
};
